import { useState } from 'react';
import { ShieldHalf } from 'lucide-react';
import { EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Card, CardBody, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import {
  useVpnGateways,
  useVpnSourceNats,
  useCreateSourceNat,
  useDeleteSourceNat,
  type VpnSourceNat,
} from '@/api/opennebula';
import { GenericList } from './GenericList';

// Source NAT rules are gateway-scoped: the list + create endpoints require a
// gateway id, so the tab fronts the list with a gateway picker.
export function SnatTab({ serviceId }: { serviceId: string }) {
  const { data: gateways = [] } = useVpnGateways(serviceId);
  const [gatewayId, setGatewayId] = useState('');
  const activeGateway = gatewayId || gateways[0]?.id || '';

  // All hooks run unconditionally (Rules of Hooks) before any early return.
  const list = useVpnSourceNats(serviceId, activeGateway);
  const remove = useDeleteSourceNat(serviceId);
  const create = useCreateSourceNat(serviceId);
  const wrappedCreate = {
    mutate: (
      p: Record<string, unknown>,
      opts?: { onSuccess?: () => void; onError?: () => void },
    ) => create.mutate({ ...p, vpn_gateway_id: activeGateway }, opts),
    isPending: create.isPending,
  };

  if (gateways.length === 0) {
    // Frame the precondition empty state in a Card so it matches the bordered
    // table empty state the sibling VPN tabs render (not a bare floating block).
    return (
      <Card>
        <CardBody>
          <EmptyState icon={<ShieldHalf size={28} />} title="No VPN gateways" description="Create a VPN gateway first — source NAT rules are scoped to a gateway." />
        </CardBody>
      </Card>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <Field label="VPN gateway">
        {() => (
          <Select value={activeGateway} onValueChange={setGatewayId}>
            <SelectTrigger style={{ maxWidth: 320 }}><SelectValue placeholder="Select a gateway" /></SelectTrigger>
            <SelectContent>
              {gateways.map((g) => <SelectItem key={g.id} value={g.id}>{g.name || g.id}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </Field>
      <GenericList title="Source NAT rules" list={list} create={wrappedCreate} remove={remove}
        fields={[{ key: 'name', label: 'Name' }, { key: 'source_addr', label: 'Source address', placeholder: '10.0.0.0/24' }, { key: 'translation_addr', label: 'Translation address', placeholder: '203.0.113.1' }]}
        columns={[
          { id: 'name', header: 'Name', cell: ({ row }) => <b style={{ fontWeight: 500 }}>{(row.original as VpnSourceNat).name || (row.original as VpnSourceNat).id}</b> },
          { id: 'source', header: 'Source', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{(row.original as VpnSourceNat).source_addr || '—'}</span> },
          { id: 'translation', header: 'Translation', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{(row.original as VpnSourceNat).translation_addr || '—'}</span> },
        ] as ColumnDef<VpnSourceNat, unknown>[]}
      />
    </div>
  );
}
